
/* injects from baggage-loader */
require('./style.styl');

import React from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {fetchIncommingTraffic} from 'redux/modules/insights/actions'
import {setTimePeriod} from 'redux/modules/currentUser/actions'
import {getIncommingTrafficData} from 'redux/modules/insights/selectors'
import {
  getCurrentTimePeriod,
  getTimePeriodRanges,
  getAggregatedSites,
  getSelectedDateRange
} from 'redux/modules/currentUser/selectors'
import MetricTable from './MetricTable'
import ChannelGroupSelector from 'containers/ChannelGroupSelector'
import TimeUnitSelector from 'components/TimeUnitSelector'
import propsChanged from 'utils/propsChanged'
import {channelGroupList, channelTypeList} from '@divvit/common-channels'
import {differenceInDays} from 'date-fns'
import moment from 'moment'

export class InCommingDataView extends React.Component {
  static propTypes = {
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    trafficData: PropTypes.array,
    fetchIncommingTraffic: PropTypes.func,
    setTimePeriod: PropTypes.func,
    changeSort: PropTypes.func,
    timePeriod: PropTypes.string,
    dynamicDateType: PropTypes.string,
    sortKey: PropTypes.string,
    sortDirection: PropTypes.string,
    isFetching: PropTypes.bool
  }

  static contextTypes = {
    currency: PropTypes.string,
    timezone: PropTypes.string
  }

  constructor(props) {
    super(props)
    this.state = {
      selectedChannels: null,
      timeUnit: 'day'
    }
  }

  componentDidMount() {
    this.fetchData()
  }

  async componentWillReceiveProps(nextProps) {
    if (propsChanged(this.props, nextProps, ['startDate', 'endDate'])) {
      const {startDate, endDate} = nextProps
      if (differenceInDays(endDate, startDate) <= 1) {
        await this.setState({timeUnit: 'hour'})
      }
      this.fetchData()
    }
  }

  render() {
    const {
      startDate,
      endDate,
      timePeriod,
      dynamicDateType,
      trafficData,
      sortKey,
      sortDirection,
      isFetching,
      setTimePeriod,
      changeSort
    } = this.props

    const {selectedChannels, channelTypes, timeUnit} = this.state

    const localTimeZone = moment.tz.guess()
    const defaultTimeZone = moment.defaultZone.name
    const lcStartDate = this.convertTimeZone(startDate, defaultTimeZone, localTimeZone)
    const lcEndDate = this.convertTimeZone(endDate, defaultTimeZone, localTimeZone)
    const standardFormat = `${lcStartDate.format('D MMM YY')} — ${lcEndDate.format('D MMM YY')}`

    const columns = [
      {
        type: 'chart',
        label: <div className='table-cell is-grow'>Metric Trend</div>,
        width: 'grow',
        dataKey: 'traffic',
        accumulatedValues: false
      },
      {
        type: 'value',
        label: 'Count',
        description: `${standardFormat}`,
        width: '100px',
        dataKey: 'total',
        format: 'number',
        applyColor: false
      }
    ]

    const conversionData = trafficData.filter(
      item =>
        [
          'order:regCount-non-direct-last-click',
          'order:ftdCount-non-direct-last-click'
        ].includes(item.metric)
    )

    const otherData = trafficData.filter(
      item =>
        [
          'session:count',
          'impression:count'
        ].includes(item.metric)
    )

    return (
      <div className='incomming-data-view'>
        <div className='incomming-data-view_display container'>
          <TimeUnitSelector
            label='Time Unit'
            timeUnit={timeUnit}
            onSelect={timeUnit => {
              this.setState({timeUnit}, () => this.fetchData())
            }}
          />
        </div>

        <MetricTable
          className='incomming-data-view_table'
          timePeriod={timePeriod}
          data={conversionData}
          isLoading={isFetching}
          disabled={isFetching}
          sortKey={sortKey}
          sortDirection={sortDirection}
          setTimePeriod={setTimePeriod}
          dynamicDateType={dynamicDateType}
          changeSort={changeSort}
          columns={columns}
        />

        <div className='incomming-data-view_filter container'>
          <div className='incomming-data-view_filter_label'>Filters</div>
          <div className='incomming-data-view_filter_channel'>
            <div className='incomming-data-view_filter_channel_label'>
              Channel
            </div>
            <ChannelGroupSelector
              className='incomming-data-view_filter_channel_select'
              groups={channelGroupList}
              channelTypes={channelTypeList}
              selected={selectedChannels}
              types={channelTypes}
              onSelect={(selectedChannels, channelTypes) => {
                this.setState({selectedChannels, channelTypes}, () => this.fetchData())
              }}
              isSingle
            />
          </div>
        </div>

        <MetricTable
          className='incomming-data-view_table'
          timePeriod={timePeriod}
          data={otherData}
          isLoading={isFetching}
          disabled={isFetching}
          sortKey={sortKey}
          sortDirection={sortDirection}
          setTimePeriod={setTimePeriod}
          dynamicDateType={dynamicDateType}
          changeSort={changeSort}
          columns={columns}
        />
      </div>
    )
  }

  fetchData() {
    const {timezone} = this.context
    const {fetchIncommingTraffic} = this.props
    const {selectedChannels, channelTypes, timeUnit} = this.state

    fetchIncommingTraffic({
      metrics: ['session:count', 'impression:count'],
      selectedChannels,
      channelTypes,
      timeUnit,
      timezone
    })

    fetchIncommingTraffic({
      metrics: [
        'order:regCount-non-direct-last-click',
        'order:ftdCount-non-direct-last-click'
      ],
      timeUnit,
      timezone
    })
  }

  convertTimeZone(time, fromTimezone, toTimezone) {
    const timeFormat = moment.tz(time, fromTimezone).format('YYYY-MM-DD HH:mm:ss')
    const mmStartDate = moment.tz(timeFormat, toTimezone)
    return mmStartDate
  }
}

const mapStateToProps = (state, props) => {
  const {isFetching} = state.insights.incommings
  const timePeriod = getCurrentTimePeriod(state)
  const timePeriodRanges = getTimePeriodRanges(state)
  const crossSiteMode = props.route.crossSiteMode
  const {startDate, endDate} = getSelectedDateRange(state)

  return {
    startDate,
    endDate,
    timePeriod,
    timePeriodRanges,
    sites: getAggregatedSites(state, crossSiteMode),
    trafficData: getIncommingTrafficData(state),
    isFetching: isFetching
  }
}

const mapDispatchToProps = {
  fetchIncommingTraffic,
  setTimePeriod
}

export default connect(mapStateToProps, mapDispatchToProps)(InCommingDataView)



// WEBPACK FOOTER //
// ./client/views/DataCollectionView/InCommingDataView/index.js